import { z } from 'zod';
import { TicketStatusSchema } from './ticket-schemas.js';
// ── SMS / Signed Ticket Token schemas (JQ4-30 · US-COMMS-001) ───────────────
//
// These schemas define the contract for signed ticket links and SMS outbox
// DTOs shared between the backend and any frontend consumers.
//
// Token format (URL query param `t`):
//   <base64url(reference)>.<base64url(hmac-sha256)>
// ── Token string ──────────────────────────────────────────────────────────────
export const SignedTokenStringSchema = z
    .string()
    .min(1)
    .regex(/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/, 'Signed token must be <base64url>.<base64url> format');
// ── SMS status ────────────────────────────────────────────────────────────────
export const SMS_STATUSES = ['queued', 'sent', 'failed'];
export const SmsStatusSchema = z.enum(SMS_STATUSES);
// ── SmsMessageDto — outbox row representation ─────────────────────────────────
export const SmsMessageDtoSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().uuid(),
    locationId: z.string().uuid(),
    ticketId: z.string().uuid(),
    /** Opaque random reference — NOT the internal ticketId (FR-06, SC-11). */
    reference: z.string().min(1),
    to: z.string().min(1),
    body: z.string().min(1),
    signedLink: z.string().url(),
    locale: z.enum(['en', 'es']),
    status: SmsStatusSchema,
    provider: z.string().min(1),
    providerMessageId: z.string().nullable(),
    errorCode: z.string().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
// ── ResendSmsRequest — Supervisor manual resend (FR-05) ───────────────────────
export const ResendSmsRequestSchema = z.object({
    ticketId: z.string().uuid(),
});
// ── ResendSmsResponse ─────────────────────────────────────────────────────────
export const ResendSmsResponseSchema = z.object({
    dispatched: z.boolean(),
    messageId: z.string().uuid().optional(),
});
// ── ListUndeliveredSmsResponse — Supervisor alert feed (FR-04) ────────────────
// QA R2-06 — the undelivered feed also carries the human-readable ticket
// reference (#number · plate) instead of only the raw ticket UUID. Optional so
// older backends/cached responses still parse.
export const UndeliveredSmsItemSchema = SmsMessageDtoSchema.extend({
    ticketNumber: z.number().int().positive().optional(),
    plate: z.string().optional(),
});
export const ListUndeliveredSmsResponseSchema = z.object({
    items: z.array(UndeliveredSmsItemSchema),
});
// ── VisitorTicketResponse — public visitor web ticket (JQ4-31 · US-COMMS-002) ─
//
// Returned by GET /visitor/ticket?t=<token>.
// MUST NOT contain any PII (name, phone, email) or payment/amount fields.
// The .strict() check in tests enforces this at the schema level.
export const VisitorTicketResponseSchema = z
    .object({
    establishmentName: z.string().min(1),
    ticketNumber: z.number().int().nonnegative(),
    plate: z.string().min(1),
    status: TicketStatusSchema,
    checkedInAt: z.string().datetime(),
    elapsedSeconds: z.number().int().nonnegative(),
    locale: z.enum(['en', 'es']),
    // JQ4-13 — org branding: optional, present when the org has configured them.
    // logoUrl is a presigned GET URL (1-hour TTL), not a stored value.
    logoUrl: z.string().optional(),
    primaryColor: z.string().optional(),
    // JQ4-17 · US-CONFIG-003 — Hotel nightly charge preview (pre-payment display).
    // Present ONLY when: location niche=hotel AND tariff mode=nightly AND ticket status NOT delivered/cancelled.
    // nightCount: number of overnight boundaries crossed since checkedInAt.
    // chargeAmountCents: total charge = (nightCount × ratePerNight) + SUM(exitFeeCents).
    // nightlyRateCents: the applied per-night rate (guest or non-guest). Allows the visitor UI
    //   to render "N night(s) × rate" without back-deriving from total (which includes exit fees).
    // exitFeeCents: accumulated exit fees total. Shown as a separate line when > 0.
    // currency: ISO 4217 currency code (e.g. 'USD', 'MXN') from location.currency — present when
    //   nightCount/chargeAmountCents are present. Consumers SHOULD use this instead of hardcoding USD.
    // All five fields are absent for non-hotel/non-nightly locations and delivered tickets.
    // .strict() is kept; these fields are explicitly declared to prevent accidental PII leaks.
    nightCount: z.number().int().nonnegative().optional(),
    chargeAmountCents: z.number().int().nonnegative().optional(),
    nightlyRateCents: z.number().int().nonnegative().optional(),
    exitFeeCents: z.number().int().nonnegative().optional(),
    currency: z.string().length(3).optional(),
    // JQ4-36 — payment status: present on all tickets once the backend is deployed.
    // Optional so older cached API responses (before migration) still parse cleanly.
    paymentStatus: z.enum(['unpaid', 'paid']).optional(),
    // JQ4-40 — paid breakdown (all optional so pre-migration responses and unpaid tickets parse cleanly).
    // Present when paymentStatus === 'paid' and columns are non-null (post-migration rows only).
    paidAt: z.string().datetime().optional(),
    // frequent-customer-memberships · Slice S4 — 'membership' added for
    // server-authoritative valet coverage (Capability 7, $0/no-PI tickets).
    paymentMethod: z.enum(['stripe', 'cash', 'membership']).optional(),
    amountPaidCents: z.number().int().nonnegative().optional(),
    tipAmountCents: z.number().int().nonnegative().optional(),
    // QA R2-03 — present once the valet marked the vehicle as ready for pickup
    // (ticket status stays `in_transit`) and while the ticket is still open.
    readyForPickupAt: z.string().datetime().optional(),
})
    .strict();
/**
 * Client-side variant of {@link VisitorTicketResponseSchema}: unknown keys are
 * STRIPPED instead of rejected. `.strict()` above is the backend-side PII guard
 * (its tests prove no extra field leaks out); a client must NOT fail-close the
 * whole ticket just because a newer backend added an optional field while an
 * older bundle is still open/cached (e.g. `readyForPickupAt`). Required fields
 * are still enforced.
 */
export const VisitorTicketClientResponseSchema = VisitorTicketResponseSchema.strip();
// ── SMS Templates — JQ4-34 · US-COMMS-005 ────────────────────────────────────
//
// Org Admins can set custom SMS check-in message templates per locale (EN / ES).
// Rules:
//   - Each template must contain {{link_ticket}} (mandatory — visitor web ticket link).
//   - Max 160 chars (standard SMS segment limit).
//   - Null (absent from DB) → JQRACE4 default template is used (fallback Esc 5).
//
// Variables available in templates: {{venue_name}}, {{link_ticket}}.
// ({{nombre_establecimiento}} is still substituted as a backward-compat alias
//  for templates saved before the token was renamed to English.)
//
// sms-checkin-time — {{checkin_time}} is also available (the ticket's
// checked_in_at, formatted in the location's timezone and the SMS language:
// 12-hour "3:42 PM" for en, 24-hour "15:42" for es). It is OPT-IN for custom
// templates — unlike {{link_ticket}}, it is NOT required and is left as
// literal text if the org's template never references it. The JQRACE4
// default template always includes it.
const LINK_TICKET_VAR = '{{link_ticket}}';
const smsTemplateField = z
    .string()
    .min(1, 'Template cannot be empty')
    .max(160, 'Template must be 160 characters or fewer');
export const UpsertSmsTemplatesRequestSchema = z
    .object({
    en: smsTemplateField,
    es: smsTemplateField,
})
    .refine((v) => v.en.includes(LINK_TICKET_VAR), {
    path: ['en'],
    message: `English template must include ${LINK_TICKET_VAR} / La plantilla en inglés debe incluir ${LINK_TICKET_VAR}`,
})
    .refine((v) => v.es.includes(LINK_TICKET_VAR), {
    path: ['es'],
    message: `Spanish template must include ${LINK_TICKET_VAR} / La plantilla en español debe incluir ${LINK_TICKET_VAR}`,
});
export const SmsTemplatesResponseSchema = z.object({
    en: z.string().nullable(),
    es: z.string().nullable(),
    /** true when at least one template is set (non-null); false means using JQRACE4 defaults. */
    isCustom: z.boolean(),
});
// ── RequestRetrievalResponse — POST /visitor/ticket/request-retrieval (JQ4-24) ─
//
// Minimal confirmation contract. No `message` key — copy is driven client-side
// from i18n keyed on `status`. NOT `.strict()` so future additive fields
// (e.g. retrievalRequestedAt for JQ4-25) are non-breaking.
export const RETRIEVAL_RESULT_STATUSES = ['requested', 'already_in_progress', 'delivered'];
export const RequestRetrievalResponseSchema = z.object({
    status: z.enum(RETRIEVAL_RESULT_STATUSES),
});
//# sourceMappingURL=sms-schemas.js.map